import ReactGA from "react-ga4";

export interface GA4ProductItem {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  index?: number;
  item_list_id?: string;
  item_list_name?: string;
}

export interface ProductData {
  id: string | number;
  modelNumber?: string;
  brand?: string;
  category?: string;
  subcategory?: string;
  grade?: string;
  price?: number;
  quantity?: number;
}

const isAdminRoute = (): boolean => {
  return window.location.pathname.startsWith('/admin');
};

const isGA4Ready = (): boolean => {
  return window.__GA4_INITIALIZED__ === true && !isAdminRoute();
};

export const normalizeProductItem = (
  product: ProductData,
  listContext?: { listId?: string; listName?: string; index?: number }
): GA4ProductItem => {
  return {
    item_id: String(product.id),
    item_name: product.modelNumber || String(product.id),
    item_brand: product.brand || undefined,
    item_category: product.category || undefined,
    item_category2: product.subcategory || undefined,
    item_variant: product.grade || undefined,
    price: product.price || undefined,
    quantity: product.quantity || 1,
    index: listContext?.index,
    item_list_id: listContext?.listId,
    item_list_name: listContext?.listName,
  };
};

export const trackViewItemList = (
  products: ProductData[],
  listId: string,
  listName: string
): void => {
  if (!isGA4Ready()) return;

  const items = products.map((product, index) =>
    normalizeProductItem(product, { listId, listName, index })
  );

  ReactGA.event("view_item_list", {
    item_list_id: listId,
    item_list_name: listName,
    items,
  });

  console.log('[GA4] view_item_list:', listName, items.length, 'items');
};

export const trackSelectItem = (
  product: ProductData,
  listContext?: { listId?: string; listName?: string; index?: number }
): void => {
  if (!isGA4Ready()) return;

  const item = normalizeProductItem(product, listContext);

  ReactGA.event("select_item", {
    item_list_id: listContext?.listId,
    item_list_name: listContext?.listName,
    items: [item],
  });

  console.log('[GA4] select_item:', item.item_name, 'from', listContext?.listName || 'direct');
};

export const trackViewItem = (product: ProductData, value?: number): void => {
  if (!isGA4Ready()) return;

  const item = normalizeProductItem(product);

  ReactGA.event("view_item", {
    currency: "USD",
    value: value || product.price || 0,
    items: [item],
  });

  console.log('[GA4] view_item:', item.item_name, 'value:', value || product.price);
};

export const trackAddToCart = (
  product: ProductData,
  quantity: number = 1,
  value?: number
): void => {
  if (!isGA4Ready()) return;

  const item = normalizeProductItem({ ...product, quantity });

  ReactGA.event("add_to_cart", {
    currency: "USD",
    value: value || (product.price || 0) * quantity,
    items: [item],
  });

  console.log('[GA4] add_to_cart:', item.item_name, 'qty:', quantity, 'value:', value || (product.price || 0) * quantity);
};

export const trackRemoveFromCart = (
  product: ProductData,
  quantity: number = 1,
  value?: number
): void => {
  if (!isGA4Ready()) return;

  const item = normalizeProductItem({ ...product, quantity });

  ReactGA.event("remove_from_cart", {
    currency: "USD",
    value: value || (product.price || 0) * quantity,
    items: [item],
  });

  console.log('[GA4] remove_from_cart:', item.item_name, 'qty:', quantity);
};

export const trackViewCart = (
  products: Array<ProductData & { quantity: number }>,
  totalValue: number
): void => {
  if (!isGA4Ready()) return;

  const items = products.map((product) => normalizeProductItem(product));

  ReactGA.event("view_cart", {
    currency: "USD",
    value: totalValue,
    items,
  });

  console.log('[GA4] view_cart:', items.length, 'items, value:', totalValue);
};

export const trackBeginCheckout = (
  products: Array<ProductData & { quantity: number }>,
  totalValue: number,
  coupon?: string
): void => {
  if (!isGA4Ready()) return;

  const items = products.map((product) => normalizeProductItem(product));

  ReactGA.event("begin_checkout", {
    currency: "USD",
    value: totalValue,
    coupon,
    items,
  });

  console.log('[GA4] begin_checkout:', items.length, 'items, value:', totalValue);
};

export const trackAddShippingInfo = (
  products: Array<ProductData & { quantity: number }>,
  totalValue: number,
  shippingTier: string
): void => {
  if (!isGA4Ready()) return;

  const items = products.map((product) => normalizeProductItem(product));

  ReactGA.event("add_shipping_info", {
    currency: "USD",
    value: totalValue,
    shipping_tier: shippingTier,
    items,
  });

  console.log('[GA4] add_shipping_info:', shippingTier);
};

export const trackAddPaymentInfo = (
  products: Array<ProductData & { quantity: number }>,
  totalValue: number,
  paymentType: string
): void => {
  if (!isGA4Ready()) return;

  const items = products.map((product) => normalizeProductItem(product));

  ReactGA.event("add_payment_info", {
    currency: "USD",
    value: totalValue,
    payment_type: paymentType,
    items,
  });

  console.log('[GA4] add_payment_info:', paymentType);
};

export interface PurchaseData {
  transactionId: string;
  value: number;
  tax?: number;
  shipping?: number;
  coupon?: string;
}

export const trackPurchase = (
  products: Array<ProductData & { quantity: number }>,
  purchaseData: PurchaseData
): void => {
  if (!isGA4Ready()) return;

  const items = products.map((product) => normalizeProductItem(product));

  ReactGA.event("purchase", {
    transaction_id: purchaseData.transactionId,
    currency: "USD",
    value: purchaseData.value,
    tax: purchaseData.tax || 0,
    shipping: purchaseData.shipping || 0,
    coupon: purchaseData.coupon,
    items,
  });

  // Google Ads conversion tracking
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'conversion', {
      'send_to': 'AW-1018823453/4H9UCOCQlOMBEJ2G6OUD',
      'value': purchaseData.value,
      'currency': 'USD',
      'transaction_id': purchaseData.transactionId
    });
    console.log('[Google Ads] conversion:', purchaseData.transactionId, 'value:', purchaseData.value);
  }

  console.log('[GA4] purchase:', purchaseData.transactionId, 'value:', purchaseData.value, 'items:', items.length);
};

export const trackSearch = (searchTerm: string): void => {
  if (!isGA4Ready()) return;

  ReactGA.event("search", {
    search_term: searchTerm,
  });

  console.log('[GA4] search:', searchTerm);
};

export const trackLogin = (method: string = "email"): void => {
  if (!isGA4Ready()) return;

  ReactGA.event("login", {
    method,
  });

  console.log('[GA4] login:', method);
};

export const trackSignUp = (method: string = "email"): void => {
  if (!isGA4Ready()) return;

  ReactGA.event("sign_up", {
    method,
  });

  console.log('[GA4] sign_up:', method);
};
